import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiMail, FiArrowRight, FiArrowLeft, FiShield, FiInfo } from "react-icons/fi";
import { NGROK } from "../../../APIs";
import AuthenticationPage from "./components/AuthenticationPage";
import UserService from "../../../services/UserService";
import { myLocalStorage } from "../../../components/StorageHelper";
import { GoPasskeyFill } from "react-icons/go";
import { IoMdUnlock } from "react-icons/io";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Method to get tenant info from SignUpForm
  const getTenantsInfo = async (email) => {
    const axiosInstance = axios.create();
    try {
      const tenants = await axiosInstance.get(
        `${NGROK}/api/get-tenants?email=${email.toLowerCase().trim()}`
      );
      return tenants;
    } catch (error) {
      return null;
    }
  };

  // Method to check if user exists from SignUpForm
  const checkIfUserExist = async (email) => {
    const tenants = await getTenantsInfo(email.toLowerCase().trim());

    if (!tenants || !tenants.data?.length) {
      return false;
    } else if (tenants?.data?.length) {
      myLocalStorage.setItem("role", tenants?.data[0].role);
      setFirstName(tenants?.data[0].firstName);
      setLastName(tenants?.data[0].lastName);
      return tenants?.data[0];
    }
  };

  // Method to initialize keycloak and register from SignUpForm
  const keycloakInitAndRegister = async (realm) => {
    try {
      setLoading(true);
      myLocalStorage.setItem("realm", realm);
      UserService.initKeycloak(realm);

      setTimeout(() => {
        UserService.doRegister(email.toLowerCase().trim(), firstName, lastName, `${NGROK}/loading`);

      }, 2000);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  // Sign in with passkey handler from SignUpForm
  const handlePasskeySignIn = async () => {
    setLoading(true);
    setError("");
    try {
      const user = await checkIfUserExist(email.toLowerCase().trim());

      if (
        user?.legacyRealm?.realmName === "whiteswan_tenants_initial" ||
        user?.realm?.keycloakAccountExists
      ) {
        setError("User with this email already exists.");
        setLoading(false);
        return;
      }

      if (!user) {
        myLocalStorage.setItem("role", "TENANT_ADMIN");
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        myLocalStorage.setItem("firstName", firstName);
        myLocalStorage.setItem("LastName", lastName);
        await keycloakInitAndRegister("whiteswan_tenants");
      } else if (
        user &&
        user.isInvitedAdmin &&
        !user.realm.keycloakAccountExists
      ) {
        myLocalStorage.setItem("role", "TENANT_ADMIN");
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        myLocalStorage.setItem("firstName", firstName);
        myLocalStorage.setItem("LastName", lastName);
        await keycloakInitAndRegister("whiteswan_tenants");
      } else if (user && !user.realm.keycloakAccountExists) {
        myLocalStorage.setItem("role", "TENANT_USER");
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        myLocalStorage.setItem("firstName", firstName);
        myLocalStorage.setItem("LastName", lastName);
        await keycloakInitAndRegister(user.tenantName);
      }
    } catch (err) {
      setError("Unable to sign in with passkey. Please try again.");
      setLoading(false);
    }
  };

  // Sign in with password & OTP handler from SignUpForm
  const handlePasswordOTPSignIn = async () => {
    setLoading(true);
    setError("");
    try {
      const user = await checkIfUserExist(email.toLowerCase().trim());

      if (
        user?.legacyRealm?.realmName === "whiteswan_tenants_initial" ||
        user?.legacyRealm?.keycloakAccountExists
      ) {
        setError("User with this email already exists.");
        setLoading(false);
        return;
      }

      if (!user) {
        myLocalStorage.setItem("role", "TENANT_ADMIN");
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        myLocalStorage.setItem("firstName", firstName);
        myLocalStorage.setItem("LastName", lastName);
        await keycloakInitAndRegister("whiteswan_tenants_legacy");
      } else if (
        user &&
        user.isInvitedAdmin &&
        !user.realm.keycloakAccountExists
      ) {
        myLocalStorage.setItem("role", "TENANT_ADMIN");
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        myLocalStorage.setItem("firstName", firstName);
        myLocalStorage.setItem("LastName", lastName);
        await keycloakInitAndRegister("whiteswan_tenants_legacy");
      } else if (
        user &&
        !user.isInvitedAdmin &&
        !user.legacyRealm.keycloakAccountExists
      ) {
        myLocalStorage.setItem("role", "TENANT_USER");
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        myLocalStorage.setItem("firstName", firstName);
        myLocalStorage.setItem("lastName", lastName);
        await keycloakInitAndRegister(`${user.legacyRealm.realmName}`);
      }
    } catch (err) {
      setError("Unable to sign in with password & OTP. Please try again.");
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      let response = await axios.post(`${NGROK}/api/auth/forgot-password/send`, {
        email: email.trim().toLowerCase(),
      });
      if (response.data) {
        setStep(2);
      } else {
        setError("Unable to verify the email address. Please try again.");
      }

    } catch (err) {
      setError(
        err?.response?.data?.message || "Unable to verify the email address. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      let response = await axios.post(`${NGROK}/api/auth/forgot-password/verify`, {
        email: email.trim().toLowerCase(),
        verificationCode: otp,
      });
      if (response.data) {
        setStep(3);
      } else {
        setError("Invalid or expired code. Please try again.");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || "Invalid or expired code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    let initials = "";
    const nameParts = name.split(" ");

    if (nameParts.length >= 2) {
      initials = `${nameParts[0][0]}${nameParts[1][0]}`;
    } else if (name.includes("@")) {
      const emailParts = name.split("@")[0].split(".");
      if (emailParts.length >= 2) {
        initials = `${emailParts[0][0]}${emailParts[1][0]}`;
      } else {
        initials = `${name[0]}${name[1]}`;
      }
    } else {
      initials = `${name[0]}${name[1]}`;
    }

    return initials.toUpperCase();
  };

  const stringToColor = (name) => {
    let hash = 0;
    let i;

    for (i = 0; i < name.length; i += 1) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
  };

  const goToSignIn = () => navigate("/signIn");

  return (
    <AuthenticationPage>
      <div className="auth-card">
        <span className="auth-card-icon">
          <FiShield />
        </span>
        <h1 className="auth-title">
          {step === 1 && "Forgot password"}
          {step === 2 && "Verify code"}
          {step === 3 && "Recovery options"}
        </h1>
        <p className="auth-subtitle">
          {step === 1 && "Enter your account email and we will send you a code."}
          {step === 2 && "Enter the code sent to your email."}
          {step === 3 && "Choose how you want to sign back in."}
        </p>

        <div className="auth-steps" aria-hidden="true">
          {[1, 2, 3].map((item) => (
            <span key={item} data-active={step === item} />
          ))}
        </div>

        <div className="auth-body">
          {step === 1 && (
            <>
              <form onSubmit={handleEmailSubmit}>
                <label className="auth-label">Email address</label>
                <div className="auth-input-wrap">
                  <FiMail className="auth-input-icon" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="auth-input"
                    required
                  />
                  <button type="submit" disabled={!email || loading} className="auth-inline-submit" aria-label="Send code">
                    {loading ? <span className="auth-spinner"></span> : <FiArrowRight />}
                  </button>
                </div>
                {error && <div className="auth-error auth-reveal">{error}</div>}
              </form>
              <div className="auth-note auth-reveal" data-delay="1" role="note" data-testid="signup-invite-disclaimer">
                <FiInfo aria-hidden="true" />
                <div>
                  <strong>Registered email required</strong>
                  <p>
                    Make sure this email already registered with the system. For SSO accounts, contact your
                    organization's IDP administrator.
                  </p>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <form onSubmit={handleOtpSubmit} className="auth-body auth-reveal" style={{ marginTop: 0 }}>
              <div>
                <button type="button" onClick={() => setStep(1)} className="auth-back">
                  <span>
                    <FiArrowLeft />
                  </span>
                  <span>{email}</span>
                </button>
              </div>
              <div>
                <label className="auth-label">6-digit code</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  placeholder="000000"
                  className="auth-input"
                  data-code="true"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
              </div>
              <button type="submit" disabled={!otp || loading} className="auth-primary">
                {loading ? "Verifying..." : "Verify code"}
              </button>
              {error && <div className="auth-error" style={{ marginTop: 0 }}>{error}</div>}
            </form>
          )}

          {step === 3 && (
            <>
              <div className="auth-identity auth-reveal">
                <span className="auth-avatar" style={{ backgroundColor: stringToColor(email) }}>
                  {getInitials(email)}
                </span>
                <div style={{ minWidth: 0 }}>
                  <p>
                    Hello, {firstName} {lastName}
                  </p>
                  <p>{email}</p>
                </div>
              </div>
              <button type="button" onClick={handlePasskeySignIn} disabled={loading} className="auth-primary auth-reveal" data-delay="1">
                <GoPasskeyFill />
                {loading ? "Processing..." : "Sign in with passkey"}
              </button>
              <button type="button" onClick={handlePasswordOTPSignIn} disabled={loading} className="auth-secondary auth-reveal" data-delay="2">
                <IoMdUnlock />
                {loading ? "Processing..." : "Sign in with password & OTP"}
              </button>
              {error && <div className="auth-error" style={{ marginTop: 0 }}>{error}</div>}
              <button type="button" onClick={() => setStep(1)} disabled={loading} className="auth-link">
                Try another email
              </button>
            </>
          )}
        </div>

        <div className="auth-footer">
          <span>
            Remembered your password?{" "}
            <button type="button" className="auth-link" onClick={goToSignIn}>
              Back to sign in
            </button>
          </span>
        </div>
      </div>
    </AuthenticationPage>
  );
};

export default ForgotPassword;
